import { getWheelCartResult } from './directjantes-cart-lots.js';
export { getWheelCartResult, normalizeWheelPcd } from './directjantes-cart-lots.js';
import { validateCartVehicles, prepareVehicleCheckout, refreshVehicleGroups } from './directjantes-cart-vehicles.js';

/**
 * Direct Jantes V36.22 - professional fields and 4 / different-width 2+2 storefront checks.
 * This is a client-side usability check, NOT an identity check or a checkout lock.
 * SIREN/SIRET checksum reference: https://github.com/InseeFrLab/validinsee
 * Vehicle details and professional attributes are saved before checkout.
 */

export function normalizeSiret(value) {
  // Keep punctuation/letters: silently deleting them could turn a typo into another number.
  return String(value ?? '').replace(/\s/g, '');
}

function passesLuhn(value) {
  let sum = 0;
  let doubleDigit = false;
  for (let i = value.length - 1; i >= 0; i--) {
    let digit = Number(value[i]);
    if (doubleDigit) {
      digit *= 2;
      if (digit > 9) digit -= 9;
    }
    sum += digit;
    doubleDigit = !doubleDigit;
  }
  return sum % 10 === 0;
}

export function getSiretError(value) {
  const siret = normalizeSiret(value);
  if (!siret) return 'Renseignez le SIRET de votre établissement.';
  if (!/^\d{14}$/.test(siret)) return 'Le SIRET doit contenir 14 chiffres. Les espaces sont acceptés.';
  if (/^(\d)\1{13}$/.test(siret) || /^0{9}/.test(siret)) {
    return 'Ce SIRET semble incorrect. Vérifiez le numéro de votre établissement.';
  }

  const siren = siret.slice(0, 9);
  if (!passesLuhn(siren)) return 'Ce SIRET semble incorrect. Vérifiez les 14 chiffres saisis.';
  const standardChecksum = passesLuhn(siret);
  // La Poste has a documented additional checksum rule for its establishments.
  const postalChecksum = siren === '356000000' &&
    [...siret].reduce((sum, digit) => sum + Number(digit), 0) % 5 === 0;
  if (!standardChecksum && !postalChecksum) {
    return 'Ce SIRET semble incorrect. Vérifiez les 14 chiffres saisis.';
  }
  return '';
}

export function getCompanyError(value) {
  return String(value ?? '').trim() ? '' : 'Renseignez la raison sociale ou le nom de votre entreprise.';
}

function getFieldError(field) {
  switch (field.dataset.djProField) {
    case 'siret': return getSiretError(field.value);
    case 'company': return getCompanyError(field.value);
    case 'professional': return field.checked ? '' : 'Confirmez que cet achat est effectué dans le cadre de votre activité professionnelle.';
    default: return '';
  }
}

function validateField(field, showError = false) {
  if (field.dataset.djProField === 'siret') {
    const normalized = normalizeSiret(field.value);
    if (field.value !== normalized) field.value = normalized;
  }
  const message = getFieldError(field);
  field.setCustomValidity(message);
  const error = document.getElementById(field.dataset.djErrorId);
  const visibleError = Boolean(message && showError);
  field.setAttribute('aria-invalid', String(visibleError));
  if (error) {
    error.textContent = visibleError ? message : '';
    error.hidden = !visibleError;
  }
  return !message;
}

function validateProfessionalFields(form, report = false) {
  const fields = Array.from(form.elements).filter(field => field.matches?.('[data-dj-pro-field]'));
  let firstInvalid = null;
  for (const field of fields) {
    if (field.dataset.djProField === 'company') field.value = field.value.trim();
    if (!validateField(field, true) && !firstInvalid) firstInvalid = field;
  }
  if (report && firstInvalid) {
    firstInvalid.focus();
    firstInvalid.reportValidity();
  }
  return firstInvalid === null;
}

function readWheelCart(form) {
  const rows = [...form.querySelectorAll('[data-dj-wheel]')];
  let lines;
  try { lines = rows.map(row => JSON.parse(row.dataset.djWheel)); }
  catch { return { valid: false, reason: 'data', lines: [] }; }
  // Wait for Horizon's quantity response so checkout cannot race a cart change.
  const updating = form.closest('cart-items-component')?.classList.contains('cart-items-disabled') ||
    rows.some((row, i) => {
      const input = row.querySelector('input[name="updates[]"]');
      return input && Number(input.value) !== lines[i]?.quantity;
    });
  return updating ? { valid: false, reason: 'pending', lines: [] } : getWheelCartResult(lines);
}

function renderWheelStatus(form, result) {
  const box = form.querySelector('directjantes-cart-lots');
  if (!box) return;
  const signature = JSON.stringify(result);
  if (box.dataset.result === signature) return;
  box.dataset.result = signature;
  box.hidden = result.valid;
  box.replaceChildren();
  if (result.valid) return;
  const title = document.createElement('strong');
  title.textContent = result.reason === 'pending' ? 'Actualisation du panier…' : 'Lot à compléter';
  const message = document.createElement('p');
  message.textContent = result.reason === 'pending'
    ? 'Patientez jusqu’à la mise à jour des quantités, puis cliquez sur « Passer commande ».'
    : result.reason === 'data'
      ? 'Le contrôle des références est indisponible. Actualisez le panier ; si le problème persiste, contactez-nous.'
      : 'Prévoyez 4 jantes identiques, ou 2+2 du même modèle, diamètre, entraxe et finition, avec deux largeurs différentes.';
  box.append(title, message);

}

function validateWheelCart(form, report = false) {
  if (!form.hasAttribute('data-dj-lots')) return true;
  const result = readWheelCart(form);
  renderWheelStatus(form, result);
  if (!result.valid && report) {
    const box = form.querySelector('directjantes-cart-lots');
    box?.focus({ preventScroll: true });
    const viewport = form.closest('[data-dj-scroll-viewport]');
    if (viewport && box) {
      viewport.scrollTop += box.getBoundingClientRect().top - viewport.getBoundingClientRect().top - 8;
    } else box?.scrollIntoView?.({ block: 'nearest' });
  }
  return result.valid;
}

if (typeof document !== 'undefined') {
  class DirectJantesCartLots extends HTMLElement {
    #observer;
    #form;
    #scheduled = false;
    #refresh = () => {
      if (this.#scheduled) return;
      this.#scheduled = true;
      queueMicrotask(() => {
        this.#scheduled = false;
        if (this.isConnected && this.#form) {
          validateWheelCart(this.#form);
          refreshVehicleGroups(this.#form);
        }
      });
    };
    connectedCallback() {
      this.#form = this.closest('form');
      if (!this.#form) return;
      this.#observer = new MutationObserver(this.#refresh);
      this.#observer.observe(this.#form.closest('cart-items-component') ?? this.#form, {
        childList: true, subtree: true, attributes: true,
        attributeFilter: ['data-dj-wheel', 'value', 'class'],
      });
      for (const type of ['input', 'change', 'quantity-selector:update']) this.#form.addEventListener(type, this.#refresh);
      this.#refresh();
    }
    disconnectedCallback() {
      this.#observer?.disconnect();
      for (const type of ['input', 'change', 'quantity-selector:update']) this.#form?.removeEventListener(type, this.#refresh);
    }
  }
  if (!customElements.get('directjantes-cart-lots')) customElements.define('directjantes-cart-lots', DirectJantesCartLots);

  // Delegation continues to work after Horizon replaces cart sections with AJAX.
  document.addEventListener('input', event => {
    const field = event.target;
    if (!field.matches?.('[data-dj-pro-field]')) return;
    validateField(field, field.getAttribute('aria-invalid') === 'true');
  });

  document.addEventListener('change', event => {
    if (event.target.matches?.('[data-dj-pro-field]')) validateField(event.target, true);
  });

  document.addEventListener('focusout', event => {
    if (event.target.matches?.('[data-dj-pro-field]')) validateField(event.target, true);
  });

  document.addEventListener('invalid', event => {
    if (event.target.matches?.('[data-dj-pro-field]')) validateField(event.target, true);
  }, true);

  // Run before the browser's constraint validation, including values restored by autofill.
  document.addEventListener('click', event => {
    const button = event.target.closest?.('[data-dj-checkout]');
    if (!button?.form || !button.form.matches('.cart-form')) return;
    if (!validateWheelCart(button.form, true) || !validateCartVehicles(button.form, true) || !validateProfessionalFields(button.form, true)) {
      event.preventDefault();
      event.stopPropagation();
    }
  }, true);

  // Also covers keyboard submission. The checkout coordinator saves vehicles and
  // cart attributes, then passes the company to Shopify's address fields.
  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches?.('.cart-form')) return;
    if (event.submitter && event.submitter.name !== 'checkout') return;
    if (!validateWheelCart(form, true) || !validateCartVehicles(form, true) || !validateProfessionalFields(form, true)) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    prepareVehicleCheckout(event, form);
  }, true);
}
